/**
 * Smartkeur design tokens, typed.
 *
 * Every value here mirrors `tokens.css`. This is the only place in the codebase
 * where hex codes are allowed to appear: component code consumes `cssVar` (or the
 * Tailwind utilities generated from `tokens.css`), never a literal.
 *
 * - `color`     raw hex — for canvases, PDF/e-mail generation, chart libraries,
 *               `<meta name="theme-color">`, and anything that cannot read a CSS
 *               custom property.
 * - `cssVar`    `var(--token)` strings — for React `style` props and for building
 *               Tailwind arbitrary values.
 *
 * Source of truth: `Smartkeur Portal.dc.html`. Deviations from the handoff README
 * are documented in `docs/design/tokens.md`.
 */

/* -------------------------------------------------------------------------- */
/* Colour                                                                     */
/* -------------------------------------------------------------------------- */

export const color = {
    // Ink — dark surfaces
    ink900: '#0C1210',
    ink800: '#101613',
    ink700: '#16211D',
    ink600: '#1D2723',
    ink500: '#2A3631',
    ink400: '#1F2B26',
    inkHoverRail: '#151D1A',
    inkHoverOnDark: '#1A231F',
    inkLoginGradientTop: '#101815',

    // Text
    textPrimary: '#0E1512',
    textSecondary: '#4A544F',
    textTertiary: '#6B7671',
    textQuaternary: '#5A645F',
    textReading: '#37423D',
    textMutedMono: '#8B948F',
    textOnDark: '#FFFFFF',
    textOnDark2: '#C7D0CC',
    textOnDark3: '#94A29C',
    textOnDark4: '#6E7A75',
    textOnDark5: '#4E5A55',
    textOnDark6: '#55605B',
    textOnDarkSoft: '#E6EBE8',

    // Contrast-safe substitutes (>= 4.5:1 in their role)
    textTertiarySafe: '#646F6A',
    textMutedMonoSafe: '#6B726E',
    textOnDark4Safe: '#7B8983',
    textOnDark5Safe: '#70827A',
    textOnDark6Safe: '#707F78',

    // Surfaces
    surfacePage: '#F4F6F4',
    surfaceBoard: '#ECEFEC',
    surfaceBoardDeep: '#DDE2DF',
    surfaceCard: '#FFFFFF',
    surfaceSubtle: '#FAFBFA',
    surfaceRowHover: '#F2F5F3',
    surfaceQuestionHead: '#F7F9F8',
    surfaceDetail: '#FFFDFA',
    surfaceChrome: '#E7EBE8',

    // Borders
    borderDefault: '#E3E7E4',
    borderSubtle: '#EDF0EE',
    borderSubtle2: '#F2F4F2',
    borderControl: '#D6DCD8',
    borderDashed: '#B9C3BE',
    borderPlaceholder: '#DDE3DF',
    borderPhoneMock: '#E9EDEA',
    borderControlStrong: '#8B8F8C',
    borderMarkerStrong: '#89908D',

    // Brand — the Smartkeur logo, one colour per role (docs/design/brand.md)
    brandGreen: '#127A66',
    brandInk: '#151E1E',
    brandSlogan: '#72767E',

    // Accent — the brand green (docs/design/tokens.md § Accent)
    accent: '#127A66',
    accentHover: '#0A6151',
    accentDark: '#0A5F4E',
    accentTint: '#E9F3F0',
    accentTint2: '#EDF5F2',
    accentTint3: '#F1F8F5',
    accentBorder: '#CFE3DC',
    accentTintHover: '#DCEBE7',
    accentOutlineBorder: '#C3D6CF',
    accentOutlineHover: '#E4EFEB',
    accentInk: '#17362E',

    // Warn
    warn: '#B45309',
    warnText: '#8A5A10',
    warnTint: '#FFF7EC',
    warnTint2: '#FFFBF4',
    warnBorder: '#F0DCBE',
    warnField: '#E9A23B',
    warnHover: '#93430A',
    warnInk: '#6B4A16',
    warnBadgeTint: '#FFF3DC',
    warnOnDarkSurface: '#1E1912',
    warnOnDarkBorder: '#3A2E1E',
    warnOnDarkTint: '#2A211A',

    // Danger
    danger: '#B42318',
    dangerText: '#8A3A32',
    dangerTint: '#FBEDEC',
    dangerTint2: '#FEF7F6',
    dangerBorder: '#E9C4C0',

    // Info / purple / neutral
    info: '#2563A8',
    infoText: '#1F4E86',
    infoTint: '#EDF2F8',
    purple: '#7A5AA8',
    purpleTint: '#F2EFF8',
    neutralTint: '#EEF1EF',
    neutralTint2: '#E7EBE8',
    neutralDot: '#CDD5D1',
    neutralDotStrong: '#919693',
    neutralScrollbar: '#C9D2CE',

    // Judgement — the report's own scale (read by the PDF from tokens.css)
    judgementGreenBg: '#CEEECF',
    judgementGreenFg: '#1C5F27',
    judgementGreenBorder: '#8DCE92',
    judgementYellowBg: '#FAEAA8',
    judgementYellowFg: '#664B00',
    judgementYellowBorder: '#E1BD47',
    judgementOrangeBg: '#FDD5AF',
    judgementOrangeFg: '#84390B',
    judgementOrangeBorder: '#F3A668',
    judgementRedBg: '#FCCDCA',
    judgementRedFg: '#8C1F17',
    judgementRedBorder: '#EB948E',
    judgementBlueBg: '#CEE3F8',
    judgementBlueFg: '#1C4C82',
    judgementBlueBorder: '#8DB6E2',

    // Image placeholders
    placeholderA: '#E4E9E6',
    placeholderB: '#EFF2F0',
    placeholderC: '#F7F9F7',
    placeholderBorder: '#DDE3DF',
    placeholderCaptionBg: '#FFFFFFCC',

    // Energy label scale
    labelA4: '#0A5F4E',
    labelA2: '#0E7C66',
    labelA: '#3E9078',
    labelB: '#7BAE7B',
    labelC: '#0E7C66',
    labelD: '#D9C48A',
    labelE: '#D9A96A',
    labelF: '#C98A63',
    labelG: '#B7695E',

    // White-label swatches
    swatchAccent: '#0E7C66',
    swatchBlue: '#1E5EFF',
    swatchOrange: '#C2410C',
    swatchSlate: '#3F3F46',

    // Overlay
    overlayScrim: '#0B100EAA',
} as const;

export type ColorToken = keyof typeof color;

/** `var(--token)` reference for every colour in `color`, same keys. */
export const cssVar = {
    ink900: 'var(--ink-900)',
    ink800: 'var(--ink-800)',
    ink700: 'var(--ink-700)',
    ink600: 'var(--ink-600)',
    ink500: 'var(--ink-500)',
    ink400: 'var(--ink-400)',
    inkHoverRail: 'var(--ink-hover-rail)',
    inkHoverOnDark: 'var(--ink-hover-on-dark)',
    inkLoginGradientTop: 'var(--ink-login-gradient-top)',

    textPrimary: 'var(--text-primary)',
    textSecondary: 'var(--text-secondary)',
    textTertiary: 'var(--text-tertiary)',
    textQuaternary: 'var(--text-quaternary)',
    textReading: 'var(--text-reading)',
    textMutedMono: 'var(--text-muted-mono)',
    textOnDark: 'var(--text-on-dark)',
    textOnDark2: 'var(--text-on-dark-2)',
    textOnDark3: 'var(--text-on-dark-3)',
    textOnDark4: 'var(--text-on-dark-4)',
    textOnDark5: 'var(--text-on-dark-5)',
    textOnDark6: 'var(--text-on-dark-6)',
    textOnDarkSoft: 'var(--text-on-dark-soft)',

    textTertiarySafe: 'var(--text-tertiary-safe)',
    textMutedMonoSafe: 'var(--text-muted-mono-safe)',
    textOnDark4Safe: 'var(--text-on-dark-4-safe)',
    textOnDark5Safe: 'var(--text-on-dark-5-safe)',
    textOnDark6Safe: 'var(--text-on-dark-6-safe)',

    surfacePage: 'var(--surface-page)',
    surfaceBoard: 'var(--surface-board)',
    surfaceBoardDeep: 'var(--surface-board-deep)',
    surfaceCard: 'var(--surface-card)',
    surfaceSubtle: 'var(--surface-subtle)',
    surfaceRowHover: 'var(--surface-row-hover)',
    surfaceQuestionHead: 'var(--surface-question-head)',
    surfaceDetail: 'var(--surface-detail)',
    surfaceChrome: 'var(--surface-chrome)',

    borderDefault: 'var(--border-default)',
    borderSubtle: 'var(--border-subtle)',
    borderSubtle2: 'var(--border-subtle-2)',
    borderControl: 'var(--border-control)',
    borderDashed: 'var(--border-dashed)',
    borderPlaceholder: 'var(--border-placeholder)',
    borderPhoneMock: 'var(--border-phone-mock)',
    borderControlStrong: 'var(--border-control-strong)',
    borderMarkerStrong: 'var(--border-marker-strong)',

    brandGreen: 'var(--brand-green)',
    brandInk: 'var(--brand-ink)',
    brandSlogan: 'var(--brand-slogan)',

    accent: 'var(--accent)',
    accentHover: 'var(--accent-hover)',
    accentDark: 'var(--accent-dark)',
    accentTint: 'var(--accent-tint)',
    accentTint2: 'var(--accent-tint-2)',
    accentTint3: 'var(--accent-tint-3)',
    accentBorder: 'var(--accent-border)',
    accentTintHover: 'var(--accent-tint-hover)',
    accentOutlineBorder: 'var(--accent-outline-border)',
    accentOutlineHover: 'var(--accent-outline-hover)',
    accentInk: 'var(--accent-ink)',
    /* Tekst op een vlak dat in `--accent` is gevuld; wit in de app, de eigen
       kleur van het bedrijf op de publieke rapportpagina. Geen tegenhanger in
       `color`: het is een alias, geen eigen waarde. */
    accentText: 'var(--accent-text)',

    warn: 'var(--warn)',
    warnText: 'var(--warn-text)',
    warnTint: 'var(--warn-tint)',
    warnTint2: 'var(--warn-tint-2)',
    warnBorder: 'var(--warn-border)',
    warnField: 'var(--warn-field)',
    warnHover: 'var(--warn-hover)',
    warnInk: 'var(--warn-ink)',
    warnBadgeTint: 'var(--warn-badge-tint)',
    warnOnDarkSurface: 'var(--warn-on-dark-surface)',
    warnOnDarkBorder: 'var(--warn-on-dark-border)',
    warnOnDarkTint: 'var(--warn-on-dark-tint)',

    danger: 'var(--danger)',
    dangerText: 'var(--danger-text)',
    dangerTint: 'var(--danger-tint)',
    dangerTint2: 'var(--danger-tint-2)',
    dangerBorder: 'var(--danger-border)',

    info: 'var(--info)',
    infoText: 'var(--info-text)',
    infoTint: 'var(--info-tint)',
    purple: 'var(--purple)',
    purpleTint: 'var(--purple-tint)',
    neutralTint: 'var(--neutral-tint)',
    neutralTint2: 'var(--neutral-tint-2)',
    neutralDot: 'var(--neutral-dot)',
    neutralDotStrong: 'var(--neutral-dot-strong)',
    neutralScrollbar: 'var(--neutral-scrollbar)',

    judgementGreenBg: 'var(--judgement-green-bg)',
    judgementGreenFg: 'var(--judgement-green-fg)',
    judgementGreenBorder: 'var(--judgement-green-border)',
    judgementYellowBg: 'var(--judgement-yellow-bg)',
    judgementYellowFg: 'var(--judgement-yellow-fg)',
    judgementYellowBorder: 'var(--judgement-yellow-border)',
    judgementOrangeBg: 'var(--judgement-orange-bg)',
    judgementOrangeFg: 'var(--judgement-orange-fg)',
    judgementOrangeBorder: 'var(--judgement-orange-border)',
    judgementRedBg: 'var(--judgement-red-bg)',
    judgementRedFg: 'var(--judgement-red-fg)',
    judgementRedBorder: 'var(--judgement-red-border)',
    judgementBlueBg: 'var(--judgement-blue-bg)',
    judgementBlueFg: 'var(--judgement-blue-fg)',
    judgementBlueBorder: 'var(--judgement-blue-border)',

    /* Het label van een oordeel: gemengd uit de kleuren hierboven (`color-mix()`
       in tokens.css), dus geen eigen waarde en geen tegenhanger in `color`. */
    judgementGreenLabelBg: 'var(--judgement-green-label-bg)',
    judgementGreenLabelFg: 'var(--judgement-green-label-fg)',
    judgementGreenLabelBorder: 'var(--judgement-green-label-border)',
    judgementYellowLabelBg: 'var(--judgement-yellow-label-bg)',
    judgementYellowLabelFg: 'var(--judgement-yellow-label-fg)',
    judgementYellowLabelBorder: 'var(--judgement-yellow-label-border)',
    judgementOrangeLabelBg: 'var(--judgement-orange-label-bg)',
    judgementOrangeLabelFg: 'var(--judgement-orange-label-fg)',
    judgementOrangeLabelBorder: 'var(--judgement-orange-label-border)',
    judgementRedLabelBg: 'var(--judgement-red-label-bg)',
    judgementRedLabelFg: 'var(--judgement-red-label-fg)',
    judgementRedLabelBorder: 'var(--judgement-red-label-border)',
    judgementBlueLabelBg: 'var(--judgement-blue-label-bg)',
    judgementBlueLabelFg: 'var(--judgement-blue-label-fg)',
    judgementBlueLabelBorder: 'var(--judgement-blue-label-border)',

    placeholderA: 'var(--placeholder-a)',
    placeholderB: 'var(--placeholder-b)',
    placeholderC: 'var(--placeholder-c)',
    placeholderBorder: 'var(--placeholder-border)',
    placeholderCaptionBg: 'var(--placeholder-caption-bg)',
    placeholderHatch: 'var(--placeholder-hatch)',
    placeholderHatchLight: 'var(--placeholder-hatch-light)',
    placeholderHatchMap: 'var(--placeholder-hatch-map)',

    labelA4: 'var(--label-a4)',
    labelA2: 'var(--label-a2)',
    labelA: 'var(--label-a)',
    labelB: 'var(--label-b)',
    labelC: 'var(--label-c)',
    labelD: 'var(--label-d)',
    labelE: 'var(--label-e)',
    labelF: 'var(--label-f)',
    labelG: 'var(--label-g)',

    swatchAccent: 'var(--swatch-accent)',
    swatchBlue: 'var(--swatch-blue)',
    swatchOrange: 'var(--swatch-orange)',
    swatchSlate: 'var(--swatch-slate)',

    overlayScrim: 'var(--overlay-scrim)',
} as const satisfies Record<string, `var(--${string})`>;

/* -------------------------------------------------------------------------- */
/* Semantic pairs                                                             */
/* -------------------------------------------------------------------------- */

/** A background/foreground pair. Never use one half without the other. */
export type TonePair = {
    readonly bg: `var(--${string})`;
    readonly fg: `var(--${string})`;
    /** Optional 1px border for card-shaped uses of the same tone. */
    readonly border?: `var(--${string})`;
    /** Contrast of fg on bg, measured. Always >= 4.5:1. */
    readonly contrast: number;
};

/** The seven order statuses, in lifecycle order. */
export const ORDER_STATUSES = [
    'nieuw',
    'bevestigd',
    'ingepland',
    'opname',
    'concept',
    'voltooid',
    'gefactureerd',
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** Dutch labels shown in the UI, in the words of `docs/woordenlijst.md`. */
export const orderStatusLabel: Record<OrderStatus, string> = {
    nieuw: 'Nieuw',
    bevestigd: 'Bevestigd',
    ingepland: 'Ingepland',
    opname: 'In opname',
    concept: 'Concept rapport',
    voltooid: 'Afgerond',
    gefactureerd: 'Gefactureerd',
};

/**
 * Fixed colour pairs per order status. Four tones cover seven statuses —
 * do not introduce a fifth (principle 9).
 */
export const orderStatusTone: Record<OrderStatus, TonePair> = {
    nieuw: { bg: cssVar.infoTint, fg: cssVar.infoText, contrast: 7.49 },
    bevestigd: { bg: cssVar.infoTint, fg: cssVar.infoText, contrast: 7.49 },
    ingepland: { bg: cssVar.neutralTint, fg: cssVar.textSecondary, contrast: 6.91 },
    opname: { bg: cssVar.warnTint, fg: cssVar.warnText, contrast: 5.57 },
    concept: { bg: cssVar.warnTint, fg: cssVar.warnText, contrast: 5.57 },
    voltooid: { bg: cssVar.accentTint2, fg: cssVar.accentDark, contrast: 6.86 },
    gefactureerd: { bg: cssVar.accentTint2, fg: cssVar.accentDark, contrast: 6.86 },
};

/** The three severity levels used for findings and building parts. */
export const SEVERITIES = ['goed', 'aandacht', 'gebrek'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const severityLabel: Record<Severity, string> = {
    goed: 'Goed',
    aandacht: 'Aandachtspunt',
    gebrek: 'Gebrek',
};

export const severityTone: Record<Severity, TonePair> = {
    goed: {
        bg: cssVar.accentTint2,
        fg: cssVar.accentDark,
        border: cssVar.accentBorder,
        contrast: 6.86,
    },
    aandacht: {
        bg: cssVar.warnTint,
        fg: cssVar.warnText,
        border: cssVar.warnBorder,
        contrast: 5.57,
    },
    gebrek: {
        bg: cssVar.dangerTint2,
        fg: cssVar.dangerText,
        border: cssVar.dangerBorder,
        contrast: 7.27,
    },
};

/** Solid dot colour per severity (findings list, lifecycle, tree). */
export const severityDot: Record<Severity, `var(--${string})`> = {
    goed: cssVar.accent,
    aandacht: cssVar.warn,
    gebrek: cssVar.danger,
};

/**
 * The colours of the report's judgement scale (oordeel), in the order of the
 * scale: Goed green, Voldoende yellow, Matig orange, Onvoldoende red, Niet
 * waarneembaar blue. Actie noodzakelijk shares red, Nader onderzoek blue. The
 * API sends the tone of a judgement; which judgement gets which tone is decided
 * there (`App\Domain\Reporting\Enums\Judgement`).
 *
 * A scale of its own, not a status: it only colours a judgement in the report,
 * the client page and the office preview, and it never uses `--accent-*`, which
 * the client page sets to the company's colour. The PDF reads the same custom
 * properties from `tokens.css`.
 *
 * The pair is the quiet label (`--judgement-<tone>-label-*`), mixed from the
 * tone's colours: a light wash, a dark muted word, a border that is barely
 * there. Draw it with `StatusPill judgement={tone} size="xs" bordered`.
 */
export const JUDGEMENT_TONES = ['green', 'yellow', 'orange', 'red', 'blue'] as const;
export type JudgementTone = (typeof JUDGEMENT_TONES)[number];

export function isJudgementTone(value: unknown): value is JudgementTone {
    return (JUDGEMENT_TONES as readonly unknown[]).includes(value);
}

export const judgementTone: Record<JudgementTone, TonePair> = {
    green: {
        bg: cssVar.judgementGreenLabelBg,
        fg: cssVar.judgementGreenLabelFg,
        border: cssVar.judgementGreenLabelBorder,
        contrast: 8.47,
    },
    yellow: {
        bg: cssVar.judgementYellowLabelBg,
        fg: cssVar.judgementYellowLabelFg,
        border: cssVar.judgementYellowLabelBorder,
        contrast: 9.05,
    },
    orange: {
        bg: cssVar.judgementOrangeLabelBg,
        fg: cssVar.judgementOrangeLabelFg,
        border: cssVar.judgementOrangeLabelBorder,
        contrast: 8.62,
    },
    red: {
        bg: cssVar.judgementRedLabelBg,
        fg: cssVar.judgementRedLabelFg,
        border: cssVar.judgementRedLabelBorder,
        contrast: 9.28,
    },
    blue: {
        bg: cssVar.judgementBlueLabelBg,
        fg: cssVar.judgementBlueLabelFg,
        border: cssVar.judgementBlueLabelBorder,
        contrast: 9.17,
    },
};

/** Generic badge/pill tones, including the two extra reds. */
export const TONES = [
    'neutral',
    'accent',
    'warn',
    'danger',
    'alarm',
    'info',
    'purple',
    'onDark',
] as const;
export type Tone = (typeof TONES)[number];

export const tone: Record<Tone, TonePair> = {
    neutral: { bg: cssVar.neutralTint, fg: cssVar.textSecondary, contrast: 6.91 },
    accent: {
        bg: cssVar.accentTint2,
        fg: cssVar.accentDark,
        border: cssVar.accentBorder,
        contrast: 6.86,
    },
    warn: { bg: cssVar.warnTint, fg: cssVar.warnText, border: cssVar.warnBorder, contrast: 5.57 },
    /** Soft red: severity "gebrek", public cost card. */
    danger: {
        bg: cssVar.dangerTint2,
        fg: cssVar.dangerText,
        border: cssVar.dangerBorder,
        contrast: 7.27,
    },
    /** Loud red: overdue invoice, missing mandatory photo. */
    alarm: {
        bg: cssVar.dangerTint,
        fg: cssVar.danger,
        border: cssVar.dangerBorder,
        contrast: 5.77,
    },
    info: { bg: cssVar.infoTint, fg: cssVar.infoText, contrast: 7.49 },
    purple: { bg: cssVar.purpleTint, fg: cssVar.purple, contrast: 4.79 },
    /** Amber on dark — the offline indicator and the rail counter. */
    onDark: { bg: cssVar.warnOnDarkSurface, fg: cssVar.warnField, contrast: 8.06 },
};

/**
 * Service colours used in planning, badges and the legend, in legend order.
 *
 * Smartkeur does the bouwtechnische keuring today. `energielabel` is for a company that
 * has that service, `nen2767` is reserved for later: the legend only shows the colours
 * of the services a company has (`legendServices`).
 */
export const SERVICES = ['keuring', 'energielabel', 'nen2767'] as const;
export type Service = (typeof SERVICES)[number];

export const serviceTone: Record<
    Service,
    { bar: `var(--${string})`; bg: `var(--${string})`; label: string }
> = {
    keuring: { bar: cssVar.accent, bg: cssVar.accentTint2, label: 'Bouwtechnische keuring' },
    energielabel: { bar: cssVar.info, bg: cssVar.infoTint, label: 'Energielabel' },
    nen2767: { bar: cssVar.purple, bg: cssVar.purpleTint, label: 'NEN 2767' },
};

/** Item progress phases in the opname tree. */
export const ITEM_PHASES = ['open', 'bezig', 'klaar'] as const;
export type ItemPhase = (typeof ITEM_PHASES)[number];

export const itemPhaseDot: Record<ItemPhase, `var(--${string})`> = {
    open: cssVar.neutralDot,
    bezig: cssVar.warn,
    klaar: cssVar.accent,
};

/** Energy label codes, top to bottom, with bar colour and bar width. */
export const ENERGY_LABELS = [
    { code: 'A++++', bar: cssVar.labelA4, width: '30%' },
    { code: 'A++', bar: cssVar.labelA2, width: '40%' },
    { code: 'A', bar: cssVar.labelA, width: '52%' },
    { code: 'B', bar: cssVar.labelB, width: '64%' },
    { code: 'C', bar: cssVar.labelC, width: '76%' },
    { code: 'D', bar: cssVar.labelD, width: '84%' },
    { code: 'E', bar: cssVar.labelE, width: '90%' },
    { code: 'F', bar: cssVar.labelF, width: '95%' },
    { code: 'G', bar: cssVar.labelG, width: '100%' },
] as const;

export type EnergyLabelCode = (typeof ENERGY_LABELS)[number]['code'];

/** The three cost blocks, in report order. */
export const COST_BLOCKS = [
    { key: 'direct', label: 'Direct', publicLabel: 'Direct', tone: 'danger' },
    { key: 'termijn', label: 'Op termijn', publicLabel: 'Op termijn', tone: 'warn' },
    { key: 'onderhoud', label: 'Verbetering', publicLabel: 'Verbetering', tone: 'neutral' },
] as const satisfies ReadonlyArray<{
    key: string;
    label: string;
    publicLabel: string;
    tone: Tone;
}>;

export type CostBlockKey = (typeof COST_BLOCKS)[number]['key'];

/** Value colour for each cost block (the number itself, not the card). */
export const costValueColor: Record<CostBlockKey, `var(--${string})`> = {
    direct: cssVar.danger,
    termijn: cssVar.warn,
    onderhoud: cssVar.textPrimary,
};

/* -------------------------------------------------------------------------- */
/* Typography                                                                 */
/* -------------------------------------------------------------------------- */

export const fontFamily = {
    display: 'var(--font-display)',
    body: 'var(--font-body)',
    mono: 'var(--font-mono)',
} as const;

export type TypeRole =
    | 'display'
    | 'h1'
    | 'h1Compact'
    | 'h2'
    | 'h2Screen'
    | 'h3Report'
    | 'cardTitle'
    | 'cardTitleLg'
    | 'kpi'
    | 'kpiMd'
    | 'kpiSm'
    | 'kpiXs'
    | 'body'
    | 'bodyLg'
    | 'bodyMedium'
    | 'bodyMediumLg'
    | 'small'
    | 'smallMedium'
    | 'caption'
    | 'captionSm'
    | 'monoLabel'
    | 'monoLabelSm'
    | 'monoLabelLg';

export type TypeSpec = {
    readonly family: string;
    readonly size: string;
    readonly weight: 400 | 500 | 600 | 700;
    readonly lineHeight: string | number;
    readonly letterSpacing?: string;
    readonly textTransform?: 'uppercase';
};

export const typography: Record<TypeRole, TypeSpec> = {
    display: {
        family: fontFamily.display,
        size: '44px',
        weight: 600,
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
    },
    h1: {
        family: fontFamily.display,
        size: '24px',
        weight: 600,
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
    },
    h1Compact: {
        family: fontFamily.display,
        size: '22px',
        weight: 600,
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
    },
    h2: {
        family: fontFamily.display,
        size: '30px',
        weight: 600,
        lineHeight: 1.1,
        letterSpacing: '-0.025em',
    },
    h2Screen: {
        family: fontFamily.display,
        size: '25px',
        weight: 600,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
    },
    h3Report: {
        family: fontFamily.display,
        size: '26px',
        weight: 600,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
    },
    cardTitle: { family: fontFamily.display, size: '15px', weight: 600, lineHeight: 1.3 },
    cardTitleLg: { family: fontFamily.display, size: '17px', weight: 600, lineHeight: 1.3 },
    kpi: {
        family: fontFamily.display,
        size: '30px',
        weight: 600,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
    },
    kpiMd: {
        family: fontFamily.display,
        size: '24px',
        weight: 600,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
    },
    kpiSm: {
        family: fontFamily.display,
        size: '22px',
        weight: 600,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
    },
    kpiXs: {
        family: fontFamily.display,
        size: '20px',
        weight: 600,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
    },
    body: { family: fontFamily.body, size: '14px', weight: 400, lineHeight: 1.5 },
    bodyLg: { family: fontFamily.body, size: '16px', weight: 400, lineHeight: 1.55 },
    bodyMedium: { family: fontFamily.body, size: '14px', weight: 500, lineHeight: 1.35 },
    bodyMediumLg: { family: fontFamily.body, size: '15px', weight: 500, lineHeight: 1.35 },
    small: { family: fontFamily.body, size: '13px', weight: 400, lineHeight: 1.5 },
    smallMedium: { family: fontFamily.body, size: '13px', weight: 500, lineHeight: 1.3 },
    caption: { family: fontFamily.body, size: '12px', weight: 400, lineHeight: 1.4 },
    captionSm: { family: fontFamily.body, size: '11px', weight: 400, lineHeight: 1.4 },
    monoLabel: {
        family: fontFamily.mono,
        size: '10px',
        weight: 400,
        lineHeight: 1.2,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    monoLabelSm: {
        family: fontFamily.mono,
        size: '9px',
        weight: 400,
        lineHeight: 1.2,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
    },
    monoLabelLg: {
        family: fontFamily.mono,
        size: '11px',
        weight: 400,
        lineHeight: 1.2,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
    },
};

/* -------------------------------------------------------------------------- */
/* Space, radius, borders, elevation                                          */
/* -------------------------------------------------------------------------- */

/** The spacing steps the design actually uses. Do not invent new ones. */
export const SPACE_STEPS = [
    2, 3, 5, 6, 7, 8, 10, 12, 14, 16, 18, 20, 22, 24, 28, 30, 34, 40, 44, 56, 68,
] as const;

export type SpaceStep = (typeof SPACE_STEPS)[number];

export const space = {
    cards: 14,
    inCard: 12,
    inCardTight: 10,
} as const;

export const screenPadding = {
    header: '20px 28px',
    headerMid: '16px 24px',
    headerCompact: '14px 22px',
    content: '22px 28px 32px',
    card: '16px',
    cardLg: '18px',
    cardHeader: '14px 16px',
} as const;

export const radius = {
    controlSm: 6,
    control: 7,
    controlLg: 8,
    cardSm: 9,
    card: 10,
    cardLg: 12,
    pill: 20,
    marker: 5,
    badge: 4,
    bezel: 22,
    round: '50%',
} as const;

export const border = {
    width: 1,
    widthActive: 2,
    marker: 1.5,
    markerChecked: 6,
    reportRule: 2,
    selectedInset: 'inset 2px 0 0 var(--accent)',
} as const;

/**
 * Shadows for the layers that float — drawers, the open list of a `Dropdown`,
 * the public report page — plus one that only exists on the presentation board.
 * Everything else uses a 1px border (principle 9).
 */
export const shadow = {
    drawer: 'var(--shadow-drawer)',
    railDrawer: 'var(--shadow-rail-drawer)',
    popover: 'var(--shadow-popover)',
    publicPage: 'var(--shadow-public-page)',
    /** Presentation board only. Never use inside the app. */
    mockupFrame: 'var(--shadow-mockup-frame)',
    focus: 'var(--shadow-focus)',
} as const;

/* -------------------------------------------------------------------------- */
/* Rail — semantic aliases for the navigation shell                           */
/* -------------------------------------------------------------------------- */

/**
 * The shell reads these instead of the raw ink/text steps, so the rail can be
 * retuned in one place. Every entry points at a primitive from `cssVar`; no new
 * colour is introduced here.
 *
 * `--rail-label` and `--rail-muted` deliberately resolve to the contrast-safe
 * substitutes (`--text-on-dark-5-safe`, `--text-on-dark-4-safe`) rather than the
 * literal prototype values — see `docs/design/tokens.md` § Contrast.
 */
export const rail = {
    bg: 'var(--rail-bg)',
    border: 'var(--rail-border)',
    item: 'var(--rail-item)',
    itemHover: 'var(--rail-item-hover)',
    itemActive: 'var(--rail-item-active)',
    itemMarker: 'var(--rail-item-marker)',
    label: 'var(--rail-label)',
    muted: 'var(--rail-muted)',
    name: 'var(--rail-name)',
    brand: 'var(--rail-brand)',
    avatarBg: 'var(--rail-avatar-bg)',
    badgeAccentBg: 'var(--rail-badge-accent-bg)',
    badgeAccentFg: 'var(--rail-badge-accent-fg)',
    badgeAmberBg: 'var(--rail-badge-amber-bg)',
    badgeAmberFg: 'var(--rail-badge-amber-fg)',
    markerWidth: 'var(--rail-marker-w)',
    markerInset: 'var(--rail-marker-inset)',
    icon: 'var(--rail-icon)',
    iconHover: 'var(--rail-icon-hover)',
    iconTile: 'var(--rail-icon-tile)',
    iconTileHover: 'var(--rail-icon-tile-hover)',
    iconActive: 'var(--rail-icon-active)',
    iconTileActive: 'var(--rail-icon-tile-active)',
} as const;

export type RailToken = keyof typeof rail;

/* -------------------------------------------------------------------------- */
/* Brand — the colours of the logo per surface                                */
/* -------------------------------------------------------------------------- */

/**
 * `BrandIcon` and `BrandLogo` read these. Three colours are new (`--brand-green`,
 * `--brand-ink`, `--brand-slogan`, taken from the client's logo files); the
 * on-dark and on-green roles point at existing text tokens, so the wordmark on
 * the rail is exactly as light as the rest of the rail.
 *
 * See `docs/design/brand.md` for which variant goes where.
 */
export const brand = {
    /** Beeldmerk and "keur", on light and on dark. */
    green: 'var(--brand-green)',
    /** "Smart" on a light surface. */
    ink: 'var(--brand-ink)',
    /** "Smart" on an ink surface (rail, dark headers, login panel). */
    inkOnDark: 'var(--brand-ink-on-dark)',
    /** The slogan on a light surface. */
    slogan: 'var(--brand-slogan)',
    /** The slogan on an ink surface. */
    sloganOnDark: 'var(--brand-slogan-on-dark)',
    /** Everything, on a green surface (app icon, green tile). */
    onGreen: 'var(--brand-on-green)',
} as const;

export type BrandToken = keyof typeof brand;

/* -------------------------------------------------------------------------- */
/* Sizing                                                                     */
/* -------------------------------------------------------------------------- */

/** Minimum touch target in opname and on the public page. */
export const TOUCH_TARGET = 44;

export const controlHeight = {
    xs: 38,
    sm: 40,
    compact: 42,
    md: 44,
    lg: 46,
    xl: 48,
} as const;

export type ControlSize = keyof typeof controlHeight;

/* -------------------------------------------------------------------------- */
/* Icons                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The icon layer (`Icon`, `IconTile`) reads these, nothing else does. Sizes are
 * numbers because lucide sets `width`/`height` attributes; the stroke is drawn
 * non-scaling, so it is the same on-screen line at every size.
 */
export const iconSize = { sm: 16, md: 18, lg: 20 } as const;

export type IconSize = keyof typeof iconSize;

/** On-screen stroke width in px (`--icon-stroke`). */
export const ICON_STROKE = 1.5;

/** Tile edge per size (`--icon-tile-*`); a tile holds the icon of the same size key. */
export const iconTileSize = { sm: 26, md: 32, lg: 40 } as const;

/** The brand-sheet tile on a light surface. The rail variant lives in `rail`. */
export const iconTile = {
    bg: 'var(--icon-tile-bg)',
    bgActive: 'var(--icon-tile-bg-active)',
    fg: 'var(--icon-tile-fg)',
} as const;

/* -------------------------------------------------------------------------- */
/* Layout and breakpoints                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Breakpoints are measured on the CONTAINER width, not the viewport — the app is
 * also rendered embedded. Use a ResizeObserver on the shell root.
 */
export const BREAKPOINTS = { wide: 1250, mid: 1000 } as const;

export const LEVELS = ['narrow', 'mid', 'wide'] as const;
export type Level = (typeof LEVELS)[number];

/** Resolve a container width to a responsive level. */
export function levelFor(containerWidth: number): Level {
    if (containerWidth >= BREAKPOINTS.wide) return 'wide';
    if (containerWidth >= BREAKPOINTS.mid) return 'mid';
    return 'narrow';
}

export const layout = {
    railWide: 228,
    railMid: 190,
    railDrawer: 246,
    opnameTree: 252,
    opnameTreeMid: 232,
    opnameTreeDrawer: 274,
    opnameFind: 304,
    opnameFindDrawer: 318,
    publicPageMax: 780,
} as const;

/** Grid template for the opname screen at each level. */
export const opnameGridColumns: Record<Level, string> = {
    wide: '252px minmax(0, 1fr) 304px',
    mid: '232px minmax(0, 1fr)',
    narrow: 'minmax(0, 1fr)',
};

/* -------------------------------------------------------------------------- */
/* Copy conventions                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The "clean" answers: an answer outside this list opens the finding detail block.
 * The sweep action does not use this list; it follows the engine's own rule in
 * `features/survey/engine/sweep.ts` and only fills questions about defects.
 */
export const CLEAN_ANSWERS = [
    'Geen gebreken',
    'Geen opmerkingen',
    'Geen opmerkingen en/of gebreken',
    'Goed',
    'Nee',
    'Niet van toepassing',
    'Niet aanwezig',
] as const;

/** Dutch number formatting used throughout the app: `€ 1.450`, `142 kWh/m²·jr`. */
export const LOCALE = 'nl-NL';

export function formatEuro(amount: number, withCents = false): string {
    return `€ ${amount.toLocaleString(LOCALE, {
        minimumFractionDigits: withCents ? 2 : 0,
        maximumFractionDigits: withCents ? 2 : 0,
    })}`;
}
